import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { EntityPreviewApi } from './EntityPreviewApi';
import type {
  EntityPreviewCreateResponse,
  EntityPreviewListResponse,
  EntityPreviewGetResponse,
} from '@schibsted/backstage-plugin-entity-preview-common';

export class EntityPreviewClient implements EntityPreviewApi {
  private baseUrlPromise?: Promise<string>;

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    if (!this.baseUrlPromise) {
      this.baseUrlPromise = this.discoveryApi.getBaseUrl('catalog');
    }

    return this.baseUrlPromise;
  }

  async createPreview(
    yaml: string,
    editingEntityId?: string,
  ): Promise<EntityPreviewCreateResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/entity-previews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ yaml, editingEntityId }),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Failed to create preview entity`);
    }

    return response.json();
  }

  async listEntities(): Promise<EntityPreviewListResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/entity-previews`);

    if (!response.ok) {
      throw new Error(
        `Failed to list preview entities: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getEntity(id: string): Promise<EntityPreviewGetResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/entity-previews/${id}`,
    );

    if (!response.ok) {
      throw new Error(`Failed to get preview entity: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteEntity(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/entity-previews/${id}`,
      {
        method: 'DELETE',
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to delete preview entity: ${response.statusText}`,
      );
    }
  }
}
